import {
  CollabType,
  UGC_BRIEF_MAX_END_DAYS,
  ugcBriefGaps,
  type BriefCompensation,
  type BriefTemplateContent,
  type UgcBriefGap,
} from '@fluenceur/types';

/**
 * The parts of a sponsored brief that validation can find incomplete, in page
 * order. The editor asks for them in blocks (`blocks/block-of-section.ts`).
 */
export const BRIEF_SECTION_KEYS = [
  'concept',
  'deliverables',
  'timeline',
  'compensation',
  'guidelines',
  'usageRights',
] as const;

/**
 * The parts of a UGC brief, in page order. Delivery takes the place of the
 * caption rules: a UGC is delivered to the brand, never published by the
 * creator.
 */
export const UGC_BRIEF_SECTION_KEYS = [
  'concept',
  'deliverables',
  'usageRights',
  'delivery',
  'guidelines',
  'timeline',
  'compensation',
] as const;

export type BriefSectionKey =
  | (typeof BRIEF_SECTION_KEYS)[number]
  | (typeof UGC_BRIEF_SECTION_KEYS)[number];

/** The sections a brief of this type shows, in order. The type never changes. */
export const briefSectionKeysFor = (collabType?: CollabType): readonly BriefSectionKey[] =>
  collabType === CollabType.UGC ? UGC_BRIEF_SECTION_KEYS : BRIEF_SECTION_KEYS;

/** The editor section holding each gap the shared UGC rule names. */
const UGC_GAP_SECTION: Record<UgcBriefGap, BriefSectionKey> = {
  deliverables: 'deliverables',
  delivery: 'delivery',
  usage_rights: 'usageRights',
  compensation: 'compensation',
  timeline: 'timeline',
};

/**
 * Design-suggested defaults for the per-recipient timeline and the usage-rights
 * duration. Never rendered as live values while the content still persists
 * null — they are only seeded into the draft on explicit user action.
 */
export const DEFAULT_FIRST_CONTENT_DAYS = 14;
export const DEFAULT_END_DAYS = 45;
export const DEFAULT_DURATION_MONTHS = 6;

/**
 * Whether the editor "Finish" / freeze step should surface soft validations.
 * These never block leaving the editor (per the design) — they only paint the
 * offending control red.
 */
export interface BriefValidation {
  noDeliverables: boolean;
  invalidTimeline: boolean;
  missingFixedAmount: boolean;
  missingGiftedProduct: boolean;
  /**
   * A gifted product is chosen but says nothing of what it is worth. Its own
   * flag, not a shade of the one above: one gap asks for a product, the other
   * for its value, and they are fixed with different controls.
   */
  missingGiftedProductValue: boolean;
  /**
   * An accepted brief is a contract, and one that grants no reuse duration or
   * no channel grants nothing. The server refuses such a send, so the library
   * card and the editor must say so before the wizard is even opened.
   */
  missingUsageRights: boolean;
  emptyTransparencyNotice: boolean;
  /** UGC only: the delivery terms are not stated. */
  missingDelivery: boolean;
  /**
   * UGC only: the end of production is set past the 45 days a UGC brief may
   * promise. The send refuses it on the same shared bound.
   */
  ugcEndDeadlineTooFar: boolean;
  /**
   * UGC only: what `ugcBriefGaps` names, the rule the send refuses on. Empty
   * on a sponsored brief, which keeps its own checks above.
   */
  ugcGaps: readonly UgcBriefGap[];
}

/**
 * A value only counts as stated when it is a real figure. Zero is not one —
 * the same rule the fixed payment has always had, and a contract naming a
 * counterpart worth nothing names no counterpart at all.
 */
const isStatedValue = (cents: number | null | undefined): boolean =>
  typeof cents === 'number' && cents > 0;

/** ISO 4217 shape, matching the send guard. Presence and shape, never a list. */
const isCurrencyCode = (currency: string | null | undefined): boolean =>
  typeof currency === 'string' && /^[A-Za-z]{3}$/.test(currency);

/**
 * Whether a gifted product carries the value that makes it a counterpart.
 *
 * A catalog product owes only its price: the send stamps the brand's currency
 * onto it, so demanding one here would refuse every catalog selection the
 * picker can make. A manual product was typed by hand, so it owes both — no
 * reader downstream can guess what 45 was counted in.
 *
 * Shared with the wizard's own send gate so the library card, the editor and
 * the tunnel cannot disagree about the same brief.
 */
export const isGiftedProductValued = (compensation: BriefCompensation): boolean => {
  const gifted = compensation.giftedProduct;
  if (!gifted) return false;
  if (gifted.source === 'catalog') {
    const products = gifted.catalogProducts ?? (gifted.catalog ? [gifted.catalog] : []);
    return products.length > 0 && products.every((product) => isStatedValue(product.priceInCents));
  }
  return isStatedValue(gifted.manual?.valueCents) && isCurrencyCode(gifted.manual?.currency);
};

/**
 * A UGC brief is judged by the shared `ugcBriefGaps`, the rule the send
 * refuses on, so the editor and the server cannot disagree. Only the timeline
 * order keeps its own flag: it is the one gap whose control says more than
 * "missing".
 */
const validateUgcBriefContent = (content: BriefTemplateContent): BriefValidation => {
  const gaps = ugcBriefGaps(content);
  const { timeline } = content;
  return {
    noDeliverables: content.deliverables.length === 0,
    invalidTimeline:
      timeline.firstContentDays !== null &&
      timeline.endDays !== null &&
      timeline.endDays <= timeline.firstContentDays,
    missingFixedAmount: gaps.includes('compensation'),
    missingGiftedProduct: false,
    missingGiftedProductValue: false,
    missingUsageRights: gaps.includes('usage_rights'),
    emptyTransparencyNotice: false,
    missingDelivery: gaps.includes('delivery'),
    ugcEndDeadlineTooFar: timeline.endDays !== null && timeline.endDays > UGC_BRIEF_MAX_END_DAYS,
    ugcGaps: gaps,
  };
};

export const validateBriefContent = (
  content: BriefTemplateContent,
  collabType: CollabType = CollabType.SPONSORED_POST
): BriefValidation => {
  if (collabType === CollabType.UGC) return validateUgcBriefContent(content);

  const { deliverables, timeline, compensation, guidelines, usageRights } = content;

  const invalidTimeline =
    timeline.firstContentDays !== null &&
    timeline.endDays !== null &&
    timeline.endDays <= timeline.firstContentDays;

  const missingFixedAmount =
    compensation.type === 'fixed_payment' &&
    (compensation.fixedPayment === null || compensation.fixedPayment.amountCents <= 0);

  const missingGiftedProduct =
    compensation.type === 'gifted_product' &&
    (compensation.giftedProduct === null ||
      (compensation.giftedProduct.source === 'catalog' &&
        !(
          compensation.giftedProduct.catalogProducts?.length ??
          (compensation.giftedProduct.catalog ? 1 : 0)
        )) ||
      (compensation.giftedProduct.source === 'manual' &&
        !compensation.giftedProduct.manual?.name?.trim()));

  // Only once a product is actually there: an absent product is a missing
  // product, and asking for its value would point at a control that is not on
  // screen yet.
  const missingGiftedProductValue =
    compensation.type === 'gifted_product' &&
    !missingGiftedProduct &&
    !isGiftedProductValued(compensation);

  const missingUsageRights =
    usageRights.durationMonths === null || usageRights.channels.length === 0;

  return {
    noDeliverables: deliverables.length === 0,
    invalidTimeline,
    missingFixedAmount,
    missingGiftedProduct,
    missingGiftedProductValue,
    missingUsageRights,
    emptyTransparencyNotice: !guidelines.caption.transparencyNotice.trim(),
    missingDelivery: false,
    ugcEndDeadlineTooFar: false,
    ugcGaps: [],
  };
};

/**
 * Whether a brief is not yet ready to be sent as a proposal. Editing stays
 * non-blocking (autosave never gates), but the library card and the editor
 * surface this so a brand knows a template can't be sent as-is before opening
 * the wizard. Mirrors the wizard's own send gate.
 */
export const isBriefValidationIncomplete = (validation: BriefValidation): boolean =>
  validation.noDeliverables ||
  validation.invalidTimeline ||
  validation.missingFixedAmount ||
  validation.missingGiftedProduct ||
  validation.missingGiftedProductValue ||
  validation.missingUsageRights ||
  validation.emptyTransparencyNotice ||
  validation.missingDelivery ||
  validation.ugcEndDeadlineTooFar ||
  validation.ugcGaps.length > 0;

/** The sections holding what `isBriefValidationIncomplete` found missing, in page order. */
export const incompleteBriefSections = (
  validation: BriefValidation,
  collabType?: CollabType
): BriefSectionKey[] => {
  const missing = new Set<BriefSectionKey>(validation.ugcGaps.map((gap) => UGC_GAP_SECTION[gap]));
  if (validation.noDeliverables) missing.add('deliverables');
  if (validation.invalidTimeline || validation.ugcEndDeadlineTooFar) missing.add('timeline');
  if (
    validation.missingFixedAmount ||
    validation.missingGiftedProduct ||
    validation.missingGiftedProductValue
  ) {
    missing.add('compensation');
  }
  if (validation.emptyTransparencyNotice) missing.add('guidelines');
  if (validation.missingUsageRights) missing.add('usageRights');
  if (validation.missingDelivery) missing.add('delivery');
  return briefSectionKeysFor(collabType).filter((key) => missing.has(key));
};

export const isBriefContentIncomplete = (
  content: BriefTemplateContent,
  collabType?: CollabType
): boolean => isBriefValidationIncomplete(validateBriefContent(content, collabType));

/**
 * Short compensation label for the library card meta line: an amount (with the product a UGC payment carries), a product, or
 * nothing.
 */
export const compensationSummary = (
  content: BriefTemplateContent,
  formatCurrency: (amountCents: number, currency: string) => string,
  toDefineLabel: string,
  giftedLabel: string,
  noneLabel: string
): string => {
  const { compensation } = content;
  if (compensation.type === 'fixed_payment') {
    // A UGC payment may carry a catalog product beside it: both are named.
    const product = compensation.giftedProduct?.catalog?.title?.trim();
    const amount =
      compensation.fixedPayment && compensation.fixedPayment.amountCents > 0
        ? formatCurrency(compensation.fixedPayment.amountCents, compensation.fixedPayment.currency)
        : toDefineLabel;
    return product ? `${amount} + ${product}` : amount;
  }
  if (compensation.type === 'gifted_product') {
    return giftedLabel;
  }
  return noneLabel;
};
